import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { Th, Td, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Warehouse, AlertTriangle } from "lucide-react";
import { Switch } from "@/components/ui/switch";

/** Lagerplatz-Stammdaten inkl. Toggle der Lagerplatzverwaltung.
 *  Alle Regeln (DEFAULT-Schutz, Bestandssperren) prüft der Server. */
export default function LocationsTab() {
  const { t } = useI18n();
  const { user } = useAuth();
  const canEdit = can(user, "inventory:locations");
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [open, setOpen] = useState(false);
  const [disableFor, setDisableFor] = useState(null); // Preview vor dem Deaktivieren
  const [edit, setEdit] = useState(null); // { id, code, name, note }
  const [busy, setBusy] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [f, setF] = useState({ warehouse_id: "", code: "", name: "", note: "" });

  const load = useCallback(() => {
    api.get("/storage-locations").then((r) => setData(r.data))
      .catch((e) => { toast.error(apiError(e)); setData({ locations: [] }); });
  }, []);
  useEffect(() => {
    load();
    api.get("/warehouses").then((r) => setWarehouses(r.data)).catch(() => {});
  }, [load]);

  const create = async () => {
    try {
      await api.post("/storage-locations", f);
      toast.success(t("loc.saved")); setOpen(false);
      setF({ warehouse_id: "", code: "", name: "", note: "" }); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/storage-locations/${edit.id}`, { name: edit.name, note: edit.note });
      toast.success(t("loc.saved")); setEdit(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggle = async (l) => {
    const reactivating = l.active === false;
    try {
      await api.put(`/storage-locations/${l.id}`, { active: reactivating });
      // Eine Reaktivierung betrifft ausschließlich das Stammdatum – zuvor umgelagerte Bestände
      // bleiben dort, wohin sie gebucht wurden.
      toast.success(reactivating ? t("loc.reactivateHint") : t("loc.saved"));
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const setManagement = async (on) => {
    if (!on) {
      try {
        const { data } = await api.get("/inventory/relocation-preview");
        setDisableFor(data);
      } catch (e) { toast.error(apiError(e)); }
      return;
    }
    try { await api.put("/tenant-config", { storage_location_management_enabled: true }); toast.success(t("loc.saved")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const confirmDisable = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/inventory/consolidate-to-default");
      await api.put("/tenant-config", { storage_location_management_enabled: false });
      toast.success(t("loc.disabledDone", { count: data.count }));
      setDisableFor(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const rows = (data?.locations || []).filter((l) => showInactive || l.active !== false);
  const inactiveCount = (data?.locations || []).filter((l) => l.active === false).length;

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="locations-tab">
      {data && (
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3 rounded-md border border-border bg-card p-4">
          <div className="max-w-2xl">
            <div className="font-medium">{t("loc.management")}</div>
            <p className="mt-1 text-xs text-muted-foreground" data-testid="locations-management-hint">
              {data.management_enabled ? t("loc.managementOn") : t("loc.managementOff")}
            </p>
          </div>
          <Switch checked={!!data.management_enabled} disabled={!isAdmin}
            onCheckedChange={setManagement} data-testid="locations-management-toggle" />
        </div>
      )}
      <div className="mb-3 flex items-center gap-4">
        {canEdit && data?.management_enabled && (
          <Button onClick={() => setOpen(true)} data-testid="new-location-btn">{t("loc.new")}</Button>
        )}
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Switch checked={showInactive} onCheckedChange={setShowInactive}
            data-testid="locations-show-inactive" />
          {t("loc.showInactive")}
          {inactiveCount > 0 && <span className="font-mono">({inactiveCount})</span>}
        </label>
      </div>
      {!data ? <div className="text-muted-foreground">{t("common.loading")}</div> :
        rows.length === 0 ? <EmptyState testId="locations-empty" icon={Warehouse} title={t("loc.emptyTitle")} hint={t("tr.sameWarehouseHint")} /> : (
          <div className="flex-1 min-h-0 overflow-auto rounded-md border border-border bg-card shadow-sm">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-muted/50"><tr>
                <Th sticky>{t("loc.code")}</Th><Th sticky>{t("loc.name")}</Th>
                <Th sticky>{t("ex.warehouse")}</Th><Th sticky>{t("loc.note")}</Th>
                <Th sticky>{t("common.status")}</Th><Th sticky right>{t("common.actions")}</Th>
              </tr></thead>
              <tbody data-testid="locations-table">
                {rows.map((l) => (
                  <tr key={l.id} className="hover:bg-muted/40 transition-colors" data-testid={`location-row-${l.code}`}>
                    <Td mono className="font-medium">{l.code}</Td>
                    <Td>{l.name}</Td>
                    <Td>{l.warehouse_name || "—"}</Td>
                    <Td className="text-muted-foreground">{l.note || "—"}</Td>
                    <Td>
                      {l.system_managed && <span className="mr-1.5 rounded-full border border-border bg-muted px-2 py-0.5 text-xs text-muted-foreground" data-testid={`location-system-${l.code}`}>{t("loc.system")}</span>}
                      <span className={`rounded-full border px-2 py-0.5 text-xs ${l.active === false ? "border-border bg-muted text-muted-foreground" : "border-primary/20 bg-primary/10 text-primary"}`}>
                        {l.active === false ? t("loc.inactive") : t("loc.active")}
                      </span>
                    </Td>
                    <Td right>
                      {canEdit && !l.system_managed && (
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" data-testid={`location-edit-${l.code}`}
                            onClick={() => setEdit({ id: l.id, code: l.code, name: l.name || "", note: l.note || "" })}>
                            {t("common.edit")}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => toggle(l)} data-testid={`location-toggle-${l.code}`}>
                            {l.active === false ? t("loc.activate") : t("loc.deactivate")}
                          </Button>
                        </div>)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent data-testid="location-dialog">
          <DialogHeader><DialogTitle>{t("loc.new")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t("ex.warehouse")}</Label>
              <Select value={f.warehouse_id} onValueChange={(v) => setF({ ...f, warehouse_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="location-warehouse"><SelectValue placeholder={t("ex.warehouse")} /></SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>{t("loc.code")}</Label>
              <Input className="mt-1.5" value={f.code} data-testid="location-code"
                onChange={(e) => setF({ ...f, code: e.target.value })} /></div>
            <div><Label>{t("loc.name")}</Label>
              <Input className="mt-1.5" value={f.name} data-testid="location-name"
                onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
            <div><Label>{t("loc.note")}</Label>
              <Input className="mt-1.5" value={f.note} data-testid="location-note"
                onChange={(e) => setF({ ...f, note: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button onClick={create} data-testid="location-save"
              disabled={!f.warehouse_id || !f.code.trim()}>{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="location-edit-dialog">
          <DialogHeader><DialogTitle>{t("common.edit")} · {edit?.code}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>{t("loc.name")}</Label>
              <Input className="mt-1.5" value={edit?.name || ""} data-testid="location-edit-name"
                onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></div>
            <div><Label>{t("loc.note")}</Label>
              <Input className="mt-1.5" value={edit?.note || ""} data-testid="location-edit-note"
                onChange={(e) => setEdit({ ...edit, note: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button onClick={saveEdit} data-testid="location-edit-save"
              disabled={!edit?.name?.trim()}>{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!disableFor} onOpenChange={(o) => !o && setDisableFor(null)}>
        <DialogContent className="max-w-2xl" data-testid="locations-disable-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive" />{t("loc.disableWarnTitle")}
          </DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {disableFor?.rows?.length ? t("loc.disableWarn") : t("loc.nothingToMove")}
          </p>
          {!!disableFor?.rows?.length && (
            <div className="max-h-64 overflow-auto rounded-md border border-border">
              <table className="w-full text-xs" data-testid="locations-disable-table">
                <thead className="bg-muted/50"><tr>
                  <Th>{t("inv.productSku")}</Th><Th>{t("ex.supplierLot")}</Th>
                  <Th>{t("ex.location")}</Th><Th right>{t("common.quantity")}</Th>
                </tr></thead>
                <tbody>
                  {disableFor.rows.map((r, i) => (
                    <tr key={`${r.sku}|${r.lot_number || ""}|${r.location_code}`} data-testid={`locations-disable-row-${i}`}>
                      <Td><span className="font-mono">{r.sku}</span> · {r.product_name}</Td>
                      <Td mono>{r.lot_number || "—"}</Td>
                      <Td mono>{r.location_code}</Td>
                      <Td right mono className="font-semibold">{r.quantity}</Td>
                    </tr>))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="destructive" onClick={confirmDisable} disabled={busy}
              data-testid="locations-disable-confirm">{t("loc.disableConfirm")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
